export type Asset = {id: string; url?: string; kind: string; model?: string; metadata?: {background_view_signature?: string}};
export type Direction = {notes: string; avoid: string; revision?: number; fixed_furniture?: string[]};
/** `furniture` is the maker's text, one piece per line, until it is saved. */
export type BackgroundDirectionDraft = Direction & {signature: string; furniture?: string};
export const furnitureText = (direction?: Direction) => (direction?.fixed_furniture || []).join('\n');
export type BackgroundEstimate = {projectId: string; target: string; signature: string; estimated_credits: number; provider: string; model: string; referenceId: string; view_id?: string; view_signature?: string; master_asset_id?: string; view_label?: string};
export type View = {id: string; label: string; instruction: string; signature: string; approved: boolean; asset?: Asset; candidates: Asset[]};
export type ViewAssignmentDraft = {signature: string; assignments: Record<string,string>};
export type Location = {
  target_id: string; name: string; description: string; signature: string;
  location_id?: string;
  shot_numbers: number[]; approved: boolean; asset?: Asset;
  candidates: Asset[]; review_reason?: string;
  /** Inside or outside, for a coat or top layer: from Iris or from the maker. */
  setting?: {value: 'inside' | 'outside'; source: 'iris' | 'maker'} | null;
  /** What the approved background has and lacks (#76); the approved asset it belongs to. */
  inventory?: Inventory;
  approved_asset_id?: string;
  direction?: Direction; generation_prompt?: string; negative_prompt?: string;
  reference_generation_prompt?: string; draft_reference?: Asset;
  views?: View[]; view_assignments_signature?: string;
  assigned_views_ready?: boolean;
  view_shots?: {id: string; number: number; camera: string; view_id: string}[];
};
export type Plan = {
  registry_signature?: string;
  known_locations?: {id: string; name: string; active: boolean; candidates: Asset[]}[];
  prepared?: boolean; ready?: boolean; reviewed?: number; required?: number;
  /** Someone wears a coat or top layer: each place says inside or outside. */
  layers_wanted?: boolean;
  /** Places from the intake that no shot uses yet, and how many of them are approved. */
  places_waiting?: number; places_waiting_approved?: number;
  unassigned_scene_ids?: string[]; locations?: Location[];
  saved_directions?: (Direction & {target_id: string; name: string})[];
};
export function backgroundReference(plan: Plan, targetId: string, referenceStatus?: string, viewId?: string) {
  const location = plan.locations?.find(item => item.target_id === targetId);
  const view = viewId ? location?.views?.find(v=>v.id===viewId) : undefined;
  const asset = location?.approved === true ? viewId ? view?.approved ? view.asset : undefined : location.asset : undefined;
  const sent = !!asset && referenceStatus === 'matched';
  const status = !targetId ? 'No location assigned' : !asset ? 'Background reference needs approval' :
    sent ? 'Approved reference sent · inspect this shot' :
    referenceStatus === 'not_generated' ? 'Background approved · ready for generation' :
    referenceStatus === 'different' ? 'This image used a different background reference' :
    referenceStatus === 'missing' ? 'Approved reference was not sent for this image' :
    'Reference use not recorded · inspect this shot';
  return {targetId, location, asset, sent, status};
}

export function backgroundDirectionDirty(location: Location, draft?: BackgroundDirectionDraft): boolean {
  return !!draft && (draft.notes.trim() !== (location.direction?.notes || '') || draft.avoid.trim() !== (location.direction?.avoid || '') ||
    (draft.furniture ?? furnitureText(location.direction)).trim() !== furnitureText(location.direction));
}

export function backgroundEstimateMatches(location: Location | undefined, estimate: BackgroundEstimate | undefined, referenceId: string, dirty = false): boolean {
  if(estimate?.view_id){
    const view=location?.views?.find(v=>v.id===estimate.view_id);
    if(!location?.approved||!view||view.signature!==estimate.view_signature||location.asset?.id!==estimate.master_asset_id||estimate.referenceId!==location.asset?.id)return false;
    referenceId=estimate.referenceId;
  }
  return !!location && !!estimate && !dirty && location.target_id === estimate.target && location.signature === estimate.signature &&
    referenceId === estimate.referenceId && (!referenceId || referenceId === location.draft_reference?.id) &&
    Number.isFinite(estimate.estimated_credits) && estimate.estimated_credits >= 0 && !!estimate.model && !!estimate.provider;
}

/** What an approved place has and does not have (#76). */
export type InventoryItem = {id: string; label: string};
export type InventoryList = {present: InventoryItem[]; absent: InventoryItem[]};
export type Inventory = InventoryList & {source: 'generation_prompt' | 'maker'; outdated: boolean; previous?: InventoryList};
